import dayjs from 'dayjs';
import { z } from 'zod';
import { LicenseTypes } from '../../../types/licenses';
import { BasicBusinessInformationSchema, type BusinessInformation } from './types/businessInformation';
import { FitnessPayload } from './types/payloads';

export const FITNESS_CENTER_CONTENT_PATH = 'licenses.steps.businessInformation.forms.fitnessCenter';

export const fitnessCenterSchema = BasicBusinessInformationSchema.extend({
  // Date user will start using music (required, must be today or future?)
  startDate: z
    .string()
    .min(1, { message: `${FITNESS_CENTER_CONTENT_PATH}.startDate.validation.required` })
    // Basic ISO date format check (YYYY-MM-DD)
    .regex(/^\d{4}-\d{2}-\d{2}$/, {
      message: `${FITNESS_CENTER_CONTENT_PATH}.startDate.validation.required`,
    }),
  // Area in square meters
  area: z
    .number({
      required_error: `${FITNESS_CENTER_CONTENT_PATH}.venueSize.validation.required`,
    })
    .int({ message: `${FITNESS_CENTER_CONTENT_PATH}.venueSize.validation.required` })
    .min(1, { message: `${FITNESS_CENTER_CONTENT_PATH}.venueSize.validation.required` }),
  // Accept checkbox boolean or string 'true'/'false' (common when serializing form data)
  hasGroupTraining: z.boolean(),
  // Number of classes with fewer than 20 participants (conditional on hasGroupTraining)
  classesUnder20: z.number().int().optional(),
  // Number of classes with more than 20 participants (conditional on hasGroupTraining)
  classesOver20: z.number().int().optional(),
}).superRefine((data, ctx) => {
  // If hasGroupTraining is true, validate the class fields
  if (data.hasGroupTraining) {
    // Classes under 20 validation
    if (data.classesUnder20 === undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `${FITNESS_CENTER_CONTENT_PATH}.classesUnder20.validation.required`,
        path: ['classesUnder20'],
      });
    } else if (!Number.isFinite(data.classesUnder20) || data.classesUnder20 < 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `${FITNESS_CENTER_CONTENT_PATH}.classesUnder20.validation.required`,
        path: ['classesUnder20'],
      });
    }

    // Classes over 20 validation
    if (data.classesOver20 === undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `${FITNESS_CENTER_CONTENT_PATH}.classesOver20.validation.required`,
        path: ['classesOver20'],
      });
    } else if (!Number.isFinite(data.classesOver20) || data.classesOver20 < 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `${FITNESS_CENTER_CONTENT_PATH}.classesOver20.validation.required`,
        path: ['classesOver20'],
      });
    }
  }
});

export type FitnessCenterForm = z.infer<typeof fitnessCenterSchema>;

export const createFitnessCenterDefaults = (): BusinessInformation => ({
  licenseCode: LicenseTypes.FitnessCenterAndTreatment,
  startDate: '',
  hasGroupTraining: false,
});

export const toPayloadFitness = (form: FitnessCenterForm): FitnessPayload => {
  return {
    musicStartDate: dayjs(form.startDate).format('YYYY-MM-DD'),
    quantity: form.area ?? 0,
    smallClasses: form.hasGroupTraining ? (form.classesUnder20 ?? 0) : 0,
    largeClasses: form.hasGroupTraining ? (form.classesOver20 ?? 0) : 0,
  };
};
